import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CalendarDays,
  ClipboardCheck,
  Plus,
  Wrench,
  Zap,
  Shield,
  Settings,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { useVisits, type ServiceVisit } from '@/hooks/useVisits';
import { ScheduleVisitDialog } from '@/components/visits/ScheduleVisitDialog';

const VISIT_TYPE_META: Record<string, { label: string; icon: typeof Wrench; color: string }> = {
  preventive: { label: 'Preventive', icon: Shield, color: 'text-blue-500' },
  repair: { label: 'Repair', icon: Wrench, color: 'text-orange-500' },
  emergency: { label: 'Emergency', icon: Zap, color: 'text-red-500' },
  commissioning: { label: 'Commissioning', icon: Settings, color: 'text-purple-500' },
  warranty_inspection: { label: 'Warranty', icon: ClipboardCheck, color: 'text-green-500' },
};

function statusBadge(status: string) {
  const map: Record<string, { label: string; className: string; icon: typeof Clock }> = {
    scheduled: { label: 'Scheduled', className: 'bg-blue-500/15 text-blue-500 border-blue-500/30', icon: Clock },
    in_progress: { label: 'In Progress', className: 'bg-wt-amber-primary/15 text-wt-amber-primary border-wt-amber-primary/30', icon: PlayCircle },
    completed: { label: 'Completed', className: 'bg-wt-green-primary/15 text-wt-green-primary border-wt-green-primary/30', icon: CheckCircle2 },
    cancelled: { label: 'Cancelled', className: 'bg-muted-foreground/15 text-muted-foreground border-muted-foreground/30', icon: XCircle },
  };
  const m = map[status] ?? map.scheduled;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={cn('text-[11px] gap-1', m.className)}>
      <Icon className="w-3 h-3" />
      {m.label}
    </Badge>
  );
}

function VisitCard({ visit }: { visit: ServiceVisit }) {
  const meta = VISIT_TYPE_META[visit.visit_type] ?? VISIT_TYPE_META.preventive;
  const Icon = meta.icon;
  const timeStr = visit.scheduled_at
    ? format(new Date(visit.scheduled_at), 'MMM d, yyyy h:mm a')
    : 'Unscheduled';
  const techLabel = visit.technician_name ?? 'Unassigned';

  return (
    <Link to={`/app/visits/${visit.id}`} className="block no-underline text-inherit">
    <Card
      className="cursor-pointer hover:border-primary/40 transition-colors"
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={cn('mt-0.5 shrink-0', meta.color)}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{visit.property_name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{meta.label}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <CalendarDays className="w-3.5 h-3.5" />
                <span>{timeStr}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Tech: {techLabel}</p>
            </div>
          </div>
          <div className="shrink-0">
            {statusBadge(visit.status)}
          </div>
        </div>
        {visit.summary && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2 pl-8">{visit.summary}</p>
        )}
      </CardContent>
    </Card>
    </Link>
  );
}

export default function Visits() {
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const statusMap: Record<string, string[] | undefined> = {
    active: ['scheduled', 'in_progress'],
    all: undefined,
  };
  const rpcStatus = statusMap[statusFilter] ?? [statusFilter];
  const { data: visits, isLoading } = useVisits({
    status: rpcStatus,
  });

  const filtered = visits ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Service Visits</h1>
          <p className="text-sm text-muted-foreground">
            {filtered.length} visit{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={() => setScheduleOpen(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Schedule Visit
        </Button>
      </div>

      <div className="flex gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ClipboardCheck className="w-12 h-12 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No visits found</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setScheduleOpen(true)}>
            Schedule your first visit
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(visit => (
            <VisitCard
              key={visit.id}
              visit={visit}
            />
          ))}
        </div>
      )}

      <ScheduleVisitDialog open={scheduleOpen} onOpenChange={setScheduleOpen} />
    </div>
  );
}
